import { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
} from '@mui/material';
import { X, Sparkles, ShieldCheck, Info, Check } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  aiDiagnosisTemplates,
  type DiagnosisMedicationLine,
  type DiagnosisSuggestionResult,
} from '../../lib/api/modules/aiFeatures';
import { safeConsole } from '../../lib/piiProtection';

interface DiagnosisSuggestionsPanelProps {
  open: boolean;
  onClose: () => void;
  diagnosis: string;
  patientId: string;
  /** Visit context — required to persist AI-suggestion consent for the rest of the visit (DPDP/ABDM). */
  visitId?: string;
  /** Apply the chosen suggestion lines into the prescription's medication list. */
  onApply: (lines: DiagnosisMedicationLine[], meta: { source: 'APPROVED_TEMPLATE' | 'AI' }) => void;
  /** When true, show the "Save as approved template" action (caches for all clinics). */
  canApprove?: boolean;
}

/**
 * Diagnosis-driven medication suggestions. Fetches an APPROVED global template (instant) or
 * AI suggestions (physician-review only) for the selected diagnosis. The doctor picks lines and
 * applies them into the prescription form — nothing is auto-prescribed. Optionally caches a
 * physician-approved set as a global template for the next doctor (any clinic).
 */
export function DiagnosisSuggestionsPanel({
  open,
  onClose,
  diagnosis,
  patientId,
  visitId,
  onApply,
  canApprove = false,
}: DiagnosisSuggestionsPanelProps) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DiagnosisSuggestionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  // DPDP/ABDM: the AI fallback sends real clinical content to Bedrock — the backend requires
  // per-visit consent, acknowledged once and persisted for the rest of the visit. A Tier-1
  // (approved-template) hit never triggers this, since no PHI leaves the system for that path.
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  const fetchSuggestions = useCallback(async (consentAcknowledged = false) => {
    if (!diagnosis.trim()) {
      setError('Select or type a diagnosis first');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await aiDiagnosisTemplates.suggest(diagnosis.trim(), patientId || undefined, visitId, consentAcknowledged);
      // Non-2xx responses surface as res.error (e.g. feature disabled, quota exceeded, consent required).
      if (res.error) {
        if (res.error.toLowerCase().includes('consent')) {
          setLoading(false);
          setConsentChecked(false);
          setConsentOpen(true);
          return;
        }
        setError(res.error);
        return;
      }
      const data = res.data;
      if (!data || !data.success) {
        setError(data?.errorMessage || 'No suggestions available for this diagnosis');
        return;
      }
      setResult(data);
      // Pre-select all lines by default (doctor can deselect).
      setSelected(new Set(data.medications.map((_, i) => i)));
    } catch (err) {
      safeConsole.error('Diagnosis suggestion failed:', err);
      setError('AI suggestions are currently unavailable');
    } finally {
      setLoading(false);
    }
  }, [diagnosis, patientId, visitId]);

  useEffect(() => {
    if (open) fetchSuggestions();
    else {
      setResult(null);
      setError(null);
      setSelected(new Set());
      setConsentOpen(false);
    }
  }, [open, fetchSuggestions]);

  const confirmConsent = () => {
    setConsentOpen(false);
    fetchSuggestions(true);
  };

  const toggle = (i: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const selectedLines = (): DiagnosisMedicationLine[] =>
    (result?.medications || []).filter((_, i) => selected.has(i));

  const handleApply = () => {
    if (!result) return;
    const lines = selectedLines();
    if (lines.length === 0) {
      toast.error('Select at least one medication to apply');
      return;
    }
    onApply(lines, { source: result.source });
    toast.success(`Applied ${lines.length} medication${lines.length > 1 ? 's' : ''}`);
    onClose();
  };

  const handleSaveAsTemplate = async () => {
    if (!result) return;
    const lines = selectedLines();
    if (lines.length === 0) {
      toast.error('Select at least one medication to save');
      return;
    }
    setSaving(true);
    try {
      const res = await aiDiagnosisTemplates.approve({
        diagnosis: result.diagnosis,
        medications: lines,
        aiRequestLogId: result.aiRequestLogId,
      });
      if (res.data?.success) {
        toast.success('Saved as approved template for this diagnosis');
      } else {
        toast.error(res.data?.errorMessage || 'Failed to save template');
      }
    } catch (err) {
      safeConsole.error('Save template failed:', err);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const isApproved = result?.source === 'APPROVED_TEMPLATE';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Sparkles size={20} />
          <Typography variant="h6" fontWeight={700}>
            Medication Suggestions
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Diagnosis + source */}
        <Stack direction="row" spacing={1} alignItems="center" mb={2} flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary">
            Diagnosis:
          </Typography>
          <Chip label={diagnosis} size="small" color="primary" variant="outlined" />
          {result && (
            isApproved ? (
              <Chip
                icon={<ShieldCheck size={14} />}
                label="Approved template"
                size="small"
                color="success"
                variant="outlined"
              />
            ) : (
              <Chip
                icon={<Sparkles size={14} />}
                label="AI suggestion — review required"
                size="small"
                color="warning"
                variant="outlined"
              />
            )
          )}
        </Stack>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {!loading && result && result.medications.length === 0 && (
          <Alert severity="info">No medications were suggested for this diagnosis.</Alert>
        )}

        {!loading && result && result.medications.length > 0 && (
          <Stack spacing={1}>
            {result.medications.map((med, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  p: 1.25,
                  border: 1,
                  borderColor: selected.has(i) ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  bgcolor: selected.has(i) ? 'action.selected' : 'transparent',
                }}
              >
                <Checkbox
                  checked={selected.has(i)}
                  onChange={() => toggle(i)}
                  size="small"
                  sx={{ p: 0.5, mt: 0.25 }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {med.name}
                    </Typography>
                    {med.masterMatched ? (
                      <Tooltip title="Matched to approved formulary (medications master)">
                        <Chip
                          icon={<Check size={12} />}
                          label="Formulary"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 10 }}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title="No exact match in formulary — verify before prescribing">
                        <Chip
                          label="Unverified"
                          size="small"
                          color="default"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 10 }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {[med.dosage, med.frequency, med.duration].filter(Boolean).join(' · ') || '—'}
                  </Typography>
                  {med.instructions && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {med.instructions}
                    </Typography>
                  )}
                  {med.rationale && (
                    <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.5 }}>
                      <Info size={13} style={{ marginTop: 2, flexShrink: 0 }} />
                      <Typography variant="caption" color="text.secondary">
                        {med.rationale}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Box>
            ))}

            {result.disclaimer && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {result.disclaimer}
                </Typography>
              </>
            )}
          </Stack>
        )}
      </DialogContent>

      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} size="small">
            Cancel
          </Button>
          {/* Offer caching only for AI suggestions a doctor has curated. */}
          {canApprove && result && !isApproved && result.medications.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleSaveAsTemplate}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={14} /> : <ShieldCheck size={14} />}
            >
              Save as approved template
            </Button>
          )}
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={handleApply}
          disabled={loading || !result || selected.size === 0}
        >
          Apply {selected.size > 0 ? `(${selected.size})` : ''}
        </Button>
      </Box>

      {/* AI-suggestion consent (DPDP Act 2023 / ABDM) — required before the AI fallback call */}
      <Dialog open={consentOpen} onClose={() => setConsentOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShieldCheck size={18} /> <span>Patient consent for AI suggestions</span>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            No approved template exists for this diagnosis, so generating suggestions sends the
            diagnosis and relevant clinical context to an AI system for analysis. Confirm the
            patient has been informed and consents, as required under the DPDP Act 2023 / ABDM.
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} />}
            label="The patient has consented to AI-assisted clinical suggestions for this visit."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsentOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!consentChecked} onClick={confirmConsent}>
            Consent given — continue
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
